import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Grid, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Card, CardContent, FormControl, InputLabel, Select,
    MenuItem, Chip, Tabs, Tab, LinearProgress
} from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { PhoneAndroid, Person, School, Download } from '@mui/icons-material';
import api from '../services/api';

const COLORS = ['#2196f3', '#4caf50', '#ff9800', '#f44336', '#9c27b0'];

const AppDownloadMonitor = () => {
    const [activeTab, setActiveTab] = useState(0);
    const [classes, setClasses] = useState([]);
    const [filters, setFilters] = useState({ classId: '' });
    const [loading, setLoading] = useState(false);

    const [summary, setSummary] = useState({
        totalStudents: 500,
        enrolled: 380,
        pending: 120,
        percentage: 76
    });

    const [classwiseData, setClasswiseData] = useState([]);
    const [studentList, setStudentList] = useState([]);
    const [staffList, setStaffList] = useState([]);

    useEffect(() => {
        fetchClasses();
        fetchSummary();
    }, []);

    const fetchClasses = async () => {
        try {
            const res = await api.get('/classes');
            setClasses(res.data.data || []);
        } catch (error) {
            console.error('Failed to fetch classes');
        }
    };

    const fetchSummary = async () => {
        try {
            const res = await api.get('/monitoring/app-downloads');
            if (res.data.data) {
                setSummary(res.data.data.summary);
                setClasswiseData(res.data.data.classwise || []);
            }
        } catch (error) {
            // Mock data
            generateMockData();
        }
    };

    const generateMockData = () => {
        setClasswiseData([
            { class: '1st', total: 45, enrolled: 38, pending: 7 },
            { class: '2nd', total: 50, enrolled: 42, pending: 8 },
            { class: '3rd', total: 48, enrolled: 40, pending: 8 },
            { class: '4th', total: 52, enrolled: 45, pending: 7 },
            { class: '5th', total: 55, enrolled: 48, pending: 7 },
            { class: '6th', total: 60, enrolled: 50, pending: 10 },
            { class: '7th', total: 58, enrolled: 48, pending: 10 },
            { class: '8th', total: 50, enrolled: 40, pending: 10 },
            { class: '9th', total: 45, enrolled: 35, pending: 10 },
            { class: '10th', total: 40, enrolled: 32, pending: 8 }
        ]);

        setStudentList([
            { id: 1, name: 'Rahul Sharma', class: '5th A', phone: '9876543210', status: 'enrolled', downloadDate: '2025-01-10' },
            { id: 2, name: 'Priya Patel', class: '5th A', phone: '9876543211', status: 'pending', downloadDate: null },
            { id: 3, name: 'Amit Kumar', class: '6th B', phone: '9876543212', status: 'enrolled', downloadDate: '2025-01-12' },
            { id: 4, name: 'Sneha Desai', class: '6th B', phone: '9876543213', status: 'pending', downloadDate: null },
            { id: 5, name: 'Vikram Singh', class: '7th A', phone: '9876543214', status: 'enrolled', downloadDate: '2025-01-08' }
        ]);

        setStaffList([
            { id: 1, name: 'Mr. Rajesh Kumar', designation: 'Teacher', phone: '9876543220', status: 'enrolled' },
            { id: 2, name: 'Ms. Priya Sharma', designation: 'Teacher', phone: '9876543221', status: 'enrolled' },
            { id: 3, name: 'Mr. Suresh Patil', designation: 'Admin', phone: '9876543222', status: 'pending' }
        ]);

        setSummary({
            totalStudents: 503,
            enrolled: 418,
            pending: 85,
            percentage: 83
        });
    };

    const SummaryCard = ({ title, value, icon, color, subtitle }) => (
        <Card sx={{ height: '100%' }}>
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography color="text.secondary" variant="body2">{title}</Typography>
                        <Typography variant="h4" sx={{ fontWeight: 'bold', color }}>{value}</Typography>
                        {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
                    </Box>
                    {icon}
                </Box>
            </CardContent>
        </Card>
    );

    const renderSummaryTab = () => (
        <>
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} md={3}>
                    <SummaryCard
                        title="Total Students"
                        value={summary.totalStudents}
                        icon={<Person sx={{ fontSize: 40, color: '#1976d2' }} />}
                        color="#1976d2"
                    />
                </Grid>
                <Grid item xs={6} md={3}>
                    <SummaryCard
                        title="App Enrolled"
                        value={summary.enrolled}
                        icon={<PhoneAndroid sx={{ fontSize: 40, color: '#4caf50' }} />}
                        color="#4caf50"
                    />
                </Grid>
                <Grid item xs={6} md={3}>
                    <SummaryCard
                        title="Pending"
                        value={summary.pending}
                        icon={<Download sx={{ fontSize: 40, color: '#ff9800' }} />}
                        color="#ff9800"
                    />
                </Grid>
                <Grid item xs={6} md={3}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography color="text.secondary" variant="body2">Progress</Typography>
                            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#9c27b0' }}>
                                {summary.percentage}%
                            </Typography>
                            <LinearProgress
                                variant="determinate"
                                value={summary.percentage}
                                sx={{ mt: 1, height: 8, borderRadius: 4 }}
                                color={summary.percentage >= 75 ? 'success' : 'warning'}
                            />
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>Class Wise App Download Progress</Typography>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={8}>
                        <ResponsiveContainer width="100%" height={400}>
                            <BarChart data={classwiseData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="class" />
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                <Bar dataKey="enrolled" fill="#4caf50" name="Enrolled" />
                                <Bar dataKey="pending" fill="#ff9800" name="Pending" />
                            </BarChart>
                        </ResponsiveContainer>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TableContainer sx={{ maxHeight: 400 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Class</TableCell>
                                        <TableCell align="right">Total</TableCell>
                                        <TableCell align="right">Enrolled</TableCell>
                                        <TableCell align="right">%</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {classwiseData.map((row, idx) => (
                                        <TableRow key={idx}>
                                            <TableCell>{row.class}</TableCell>
                                            <TableCell align="right">{row.total}</TableCell>
                                            <TableCell align="right">{row.enrolled}</TableCell>
                                            <TableCell align="right">
                                                <Chip
                                                    label={`${Math.round((row.enrolled / row.total) * 100)}%`}
                                                    size="small"
                                                    color={row.enrolled / row.total >= 0.75 ? 'success' : 'warning'}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid>
                </Grid>
            </Paper>
        </>
    );

    const renderStudentListTab = () => (
        <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">Student Download Status</Typography>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Filter</InputLabel>
                    <Select value={filters.classId} label="Filter" onChange={e => setFilters({ ...filters, classId: e.target.value })}>
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="enrolled">Enrolled</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                    </Select>
                </FormControl>
            </Box>
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Class</TableCell>
                            <TableCell>Phone</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Download Date</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {studentList.map(s => (
                            <TableRow key={s.id}>
                                <TableCell>{s.name}</TableCell>
                                <TableCell>{s.class}</TableCell>
                                <TableCell>{s.phone}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={s.status}
                                        size="small"
                                        color={s.status === 'enrolled' ? 'success' : 'warning'}
                                    />
                                </TableCell>
                                <TableCell>{s.downloadDate || '-'}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );

    const renderStaffListTab = () => (
        <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Staff Download Status</Typography>
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Designation</TableCell>
                            <TableCell>Phone</TableCell>
                            <TableCell>Status</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {staffList.map(s => (
                            <TableRow key={s.id}>
                                <TableCell>{s.name}</TableCell>
                                <TableCell>{s.designation}</TableCell>
                                <TableCell>{s.phone}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={s.status}
                                        size="small"
                                        color={s.status === 'enrolled' ? 'success' : 'warning'}
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>App Download Progress</Typography>

            <Paper sx={{ mb: 2 }}>
                <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
                    <Tab label="Summary" icon={<School />} iconPosition="start" />
                    <Tab label="Student List" icon={<Person />} iconPosition="start" />
                    <Tab label="Staff List" />
                </Tabs>
            </Paper>

            {activeTab === 0 && renderSummaryTab()}
            {activeTab === 1 && renderStudentListTab()}
            {activeTab === 2 && renderStaffListTab()}
        </Box>
    );
};

export default AppDownloadMonitor;
